import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Carga rápida con teclado: navegación de desplegables sin tocar el mouse.
//
// El taller carga de a muchos registros seguidos (veinte repuestos en una OTC,
// una tanda de partes diarios). Hasta acá, elegir una opción de un desplegable
// exigía sí o sí el mouse: los combos resolvían la selección con onClick, sin
// ningún manejo de teclado. Este hook concentra ese comportamiento una sola vez
// para que TODOS los selectores del sistema se comporten igual — que es la
// diferencia entre "se puede usar con teclado" y "se puede confiar en el
// teclado", porque un solo campo que se sale del patrón obliga a soltar la mano
// del teclado y arruina el ritmo de toda la carga.
//
// Teclas:
//   ↓ / ↑        mover el resaltado (desde cerrado, ↓ abre)
//   Home / End   primera / última opción
//   PageDown/Up  saltar de a 10
//   Enter        elegir la opción resaltada
//   Tab          elegir la resaltada y seguir al campo siguiente (ver abajo)
//   Escape       cerrar sin cambiar el valor
//
// Sobre Tab: al tabular con el desplegable abierto y una opción resaltada, se
// confirma esa opción. Es lo que espera quien viene tipeando — el desplegable
// se siente como parte del campo, no como un diálogo aparte —, y evita el error
// silencioso de tabular creyendo que se eligió algo cuando en realidad el campo
// quedó vacío.
// ─────────────────────────────────────────────────────────────────────────────

const TECLAS_MANEJADAS = new Set([
  'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp', 'Enter', 'Escape', 'Tab',
]);

const SALTO_PAGINA = 10;

// Elementos que pueden recibir foco al saltar de campo. Se excluyen a propósito
// los botones auxiliares (agregar FK, escanear QR, limpiar), que van marcados
// con tabindex="-1" y por eso no entran en esta consulta.
const SELECTOR_FOCUSABLE = [
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'button',
  'a[href]',
  '[tabindex]',
].map((s) => `${s}:not([disabled]):not([tabindex="-1"])`).join(',');

// Para el autofoco al abrir: sólo campos donde se pueda escribir o elegir. Los
// botones quedan afuera a propósito — el primer destino no puede ser "Cancelar".
const SELECTOR_PRIMER_CAMPO = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"])',
  'select',
  'textarea',
].map((s) => `${s}:not([disabled]):not([tabindex="-1"])`).join(',');

// Marca que lee SearchSuggestField en su propio `onFocus`: si está presente,
// esta vez NO despliega la lista completa. El foco automático al abrir un
// formulario debe dejar el cursor listo para escribir, pero no debe disparar
// el mismo efecto que un click real del usuario (que sí espera ver la lista).
const ATTR_SUPRIMIR_APERTURA = 'data-suprimir-apertura-en-foco';

const esVisible = (el) => Boolean(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

/**
 * Mueve el foco al campo siguiente (o anterior) en orden visual del documento.
 * Se usa para el salto automático tras confirmar una opción con Enter.
 *
 * No usa `tabIndex` numérico ni un orden declarado a mano: recorre el DOM real,
 * así que un campo que se agrega o se oculta después no deja el recorrido roto.
 */
export function enfocarCampoRelativo(desdeEl, direccion = 1) {
  if (!desdeEl) return false;
  const ambito = desdeEl.closest('form') || desdeEl.closest('[data-campo-ambito]') || document.body;
  const focusables = Array.from(ambito.querySelectorAll(SELECTOR_FOCUSABLE)).filter(esVisible);
  const indice = focusables.indexOf(desdeEl);
  if (indice === -1) return false;
  return enfocarPorIndice(focusables, indice + direccion);
}

function enfocarPorIndice(focusables, indice) {
  const objetivo = focusables[indice];
  if (!objetivo) return false;
  objetivo.focus();
  // Si el destino es un campo de texto, dejar el cursor listo para escribir
  // encima: en carga rápida casi siempre se reemplaza el valor, no se edita.
  if (typeof objetivo.select === 'function' && objetivo.tagName === 'INPUT') {
    try { objetivo.select(); } catch { /* algunos tipos de input no soportan select() */ }
  }
  return true;
}

/**
 * Prepara el salto al campo siguiente ANTES de que el campo actual cambie.
 *
 * Devuelve una función que ejecuta el salto. Hace falta esta forma en dos pasos
 * porque varios combos del sistema se desmontan y vuelven a montar al elegir una
 * opción (el campo se redefine en cada render del formulario): para cuando toca
 * mover el foco, el nodo original ya no está en el documento y buscarlo en la
 * lista devuelve -1. Por eso se resuelven acá la posición y el ámbito —mientras
 * el campo todavía existe— y después se salta por índice, que sobrevive al
 * remontaje porque la cantidad y el orden de los campos no cambian.
 */
export function prepararSaltoAlSiguiente(desdeEl, direccion = 1) {
  if (!desdeEl) return () => false;
  const ambito = desdeEl.closest('form') || desdeEl.closest('[data-campo-ambito]') || document.body;
  const focusables = Array.from(ambito.querySelectorAll(SELECTOR_FOCUSABLE)).filter(esVisible);
  const indiceOrigen = focusables.indexOf(desdeEl);
  if (indiceOrigen === -1) return () => false;

  // Marca temporal para reencontrar el campo de origen después del repintado.
  // Hace falta una marca propia porque las señas naturales no sirven: el nodo
  // puede remontarse (React lo recrea en varios formularios) y el placeholder
  // cambia solo al entrar en modo búsqueda, así que buscar por placeholder
  // devuelve -1 justo cuando más se lo necesita.
  const MARCA = 'data-salto-origen';
  desdeEl.setAttribute(MARCA, '1');

  return () => {
    const vigentes = Array.from(ambito.querySelectorAll(SELECTOR_FOCUSABLE)).filter(esVisible);

    let posicion = vigentes.findIndex((el) => el.hasAttribute(MARCA));
    if (posicion === -1) posicion = vigentes.indexOf(desdeEl);
    if (posicion === -1) posicion = indiceOrigen;

    const ok = enfocarPorIndice(vigentes, posicion + direccion);

    // La marca se limpia siempre, incluso si no hubo destino: si quedara
    // pegada, el próximo salto del formulario partiría desde este campo.
    ambito.querySelectorAll(`[${MARCA}]`).forEach((el) => el.removeAttribute(MARCA));
    desdeEl.removeAttribute(MARCA);

    return ok;
  };
}

// NOTA sobre campos en cascada (un campo cuyas opciones dependen de otro).
//
// El salto sólo alcanza campos que estén habilitados EN EL MOMENTO de saltar.
// De ahí salen las dos reglas para que una cascada funcione con teclado, las dos
// del lado del formulario y no de este hook:
//
// 1. El campo dependiente NO debe definirse dentro del render del formulario.
//    Si se define adentro, React lo remonta en cada render y el remontaje se
//    lleva puesto el foco recién posicionado (terminaba en el body). Fue el caso
//    de FormularioBase con obra -> sector.
//
// 2. Mientras carga sus opciones, el campo debe quedar HABILITADO, no
//    deshabilitado. Deshabilitado no figura entre los enfocables y el salto se
//    lo pasa justo al que sigue en la carga. La forma correcta es distinguir
//    "todavía no corresponde" (deshabilitado) de "las estoy trayendo"
//    (habilitado, con el placeholder avisando). Así el foco llega al campo y
//    espera ahí a que carguen las opciones, en vez de que el cursor se mueva
//    solo cientos de milisegundos después encima de lo que el usuario ya está
//    tipeando. Ver `cargandoCisternas` en FormularioDespachoCombustible.



/**
 * Enfoca el primer campo editable dentro de un contenedor, al abrirse.
 *
 * Sirve para que un formulario recién abierto se pueda empezar a completar
 * escribiendo, sin tener que ir primero al primer campo con el mouse o con Tab.
 * En una carga de muchos registros seguidos ese gesto se repite una vez por
 * registro.
 *
 * Se saltean los campos de sólo lectura y los botones: el primer destino tiene
 * que ser algo donde se pueda escribir, no el botón "Cancelar".
 *
 * @param {object}  ref      Ref al contenedor (el <form> o el modal).
 * @param {boolean} activo   Si corresponde enfocar (p. ej. el modal está abierto).
 * @param {*}       trigger  Valor opcional para forzar un nuevo enfoque sin que
 *                           `activo` cambie — p. ej. "Guardar y nuevo" deja el
 *                           modal abierto (activo sigue en `true`) pero limpia el
 *                           formulario para cargar otro registro. Cambiar este
 *                           valor (un contador, por ejemplo) reenfoca el primer
 *                           campo igual que si el modal se acabara de abrir.
 */
export function useAutoFocoPrimerCampo(ref, activo = true, trigger = null) {
  useEffect(() => {
    if (!activo || !ref?.current) return undefined;

    // Se espera un frame: al abrir un modal, el contenido puede montarse después
    // del contenedor, y enfocar antes no encuentra ningún campo.
    const id = requestAnimationFrame(() => {
      const contenedor = ref.current;
      if (!contenedor) return;

      // Si algo dentro del formulario ya tomó el foco (un autoFocus propio),
      // no se lo pisa.
      if (contenedor.contains(document.activeElement) && document.activeElement !== document.body) {
        return;
      }

      const candidatos = Array.from(contenedor.querySelectorAll(SELECTOR_PRIMER_CAMPO))
        .filter(esVisible)
        .filter((el) => !el.readOnly);

      const primero = candidatos[0];
      if (!primero) return;
      // Si el campo es un combo con búsqueda (SearchSuggestField), su propio
      // `onFocus` lee este atributo para no desplegar la lista completa esta
      // vez — el foco automático deja el cursor listo, pero abrir la lista es
      // un gesto que le corresponde al usuario (click, flecha, tipear).
      primero.setAttribute(ATTR_SUPRIMIR_APERTURA, 'true');
      primero.focus();
      requestAnimationFrame(() => primero.removeAttribute(ATTR_SUPRIMIR_APERTURA));
      // Deja el contenido seleccionado para poder escribir encima: en un alta el
      // campo suele venir vacío, pero en una edición evita tener que borrar.
      if (typeof primero.select === 'function' && primero.tagName === 'INPUT') {
        try { primero.select(); } catch { /* algunos tipos de input no soportan select() */ }
      }
    });

    return () => cancelAnimationFrame(id);
  }, [ref, activo, trigger]);
}

/**
 * Navegación por teclado para un desplegable de opciones.
 *
 * @param {object}   opciones
 * @param {Array}    opciones.items         Lista visible actual (ya filtrada).
 * @param {boolean}  opciones.abierto       Si el desplegable está desplegado.
 * @param {Function} opciones.setAbierto    Abrir/cerrar el desplegable.
 * @param {Function} opciones.onElegir      (item) => void. Confirmar una opción.
 * @param {Function} opciones.onCerrar      Cerrar descartando (Escape / Tab sin resaltado).
 * @param {boolean}  opciones.saltarAlSiguiente  Enter salta al campo siguiente
 *        tras elegir. Se apaga en las zonas de alta de renglones, donde Enter
 *        significa "agregar el renglón" y el salto lo maneja esa zona.
 * @param {Function} opciones.onCargarMas   Traer más resultados al llegar al final.
 * @param {boolean}  opciones.hayMas        Si quedan resultados por traer.
 * @param {boolean}  opciones.cerrarAlElegir  Cerrar el desplegable al confirmar.
 *        Se apaga en los desplegables de selección múltiple (checkboxes), donde
 *        Enter alterna una opción y la lista tiene que seguir abierta para
 *        marcar la siguiente.
 */
export default function useNavegacionTeclado({
  items,
  abierto,
  setAbierto,
  onElegir,
  onCerrar,
  saltarAlSiguiente = true,
  onCargarMas,
  hayMas = false,
  cerrarAlElegir = true,
} = {}) {
  const lista = useMemo(() => items || [], [items]);
  const [indiceActivo, setIndiceActivo] = useState(-1);
  const refsOpciones = useRef([]);
  const contenedorRef = useRef(null);

  // Al cerrar, o al cambiar el conjunto de resultados, el resaltado se reinicia.
  // Si no, tras tipear una letra más el resaltado quedaría sobre la fila N de
  // una lista nueva y corta — apuntando a algo que el usuario nunca miró.
  useEffect(() => {
    if (!abierto) setIndiceActivo(-1);
  }, [abierto]);

  useEffect(() => {
    setIndiceActivo(-1);
  }, [lista.length]);

  // Mantener a la vista la opción resaltada mientras se recorre con las flechas.
  useEffect(() => {
    if (indiceActivo < 0) return;
    const el = refsOpciones.current[indiceActivo];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [indiceActivo]);

  // Scroll infinito por teclado: al resaltar cerca del final, pedir la tanda
  // siguiente. Sin esto, con búsqueda contra el servidor las flechas topan en el
  // último resultado traído y parece que no hay más — el mismo final silencioso
  // que se evita con el aviso del pie cuando se usa el mouse.
  useEffect(() => {
    if (!abierto || !onCargarMas || !hayMas) return;
    if (indiceActivo >= 0 && indiceActivo >= lista.length - 3) onCargarMas();
  }, [indiceActivo, lista.length, abierto, onCargarMas, hayMas]);

  const registrarOpcion = useCallback((indice) => (el) => {
    refsOpciones.current[indice] = el;
  }, []);

  const mover = useCallback((delta) => {
    setIndiceActivo((actual) => {
      if (!lista.length) return -1;
      if (actual === -1) return delta > 0 ? 0 : lista.length - 1;
      const siguiente = actual + delta;
      // Tope en los extremos en vez de dar la vuelta: al cargar rápido, un salto
      // del último al primero se lee como que la lista se movió sola.
      if (siguiente < 0) return 0;
      if (siguiente > lista.length - 1) return lista.length - 1;
      return siguiente;
    });
  }, [lista.length]);

  const confirmar = useCallback((item, evento) => {
    // El salto se resuelve ANTES de cambiar el estado, mientras el campo actual
    // sigue en el documento (ver prepararSaltoAlSiguiente).
    const origen = evento?.currentTarget || evento?.target || null;
    const saltar = saltarAlSiguiente && origen ? prepararSaltoAlSiguiente(origen, 1) : null;
    onElegir?.(item);
    if (cerrarAlElegir) {
      setIndiceActivo(-1);
      setAbierto?.(false);
    }
    if (saltar) {
      // Doble rAF a propósito. Un solo frame corre ANTES de que React haya
      // aplicado el cierre del desplegable, y el remontaje posterior se lleva
      // puesto el foco recién puesto: termina en el body. El segundo frame cae
      // después del repintado, cuando el DOM ya es el definitivo.
      requestAnimationFrame(() => { requestAnimationFrame(saltar); });
    }
  }, [onElegir, setAbierto, saltarAlSiguiente, cerrarAlElegir]);

  const onKeyDown = useCallback((evento) => {
    if (!TECLAS_MANEJADAS.has(evento.key)) return;

    // Combinaciones con modificadores son atajos del navegador o del sistema.
    if (evento.altKey || evento.ctrlKey || evento.metaKey) return;

    const itemActivo = indiceActivo >= 0 ? lista[indiceActivo] : null;

    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        if (!abierto) { setAbierto?.(true); setIndiceActivo(0); return; }
        mover(1);
        return;

      case 'ArrowUp':
        evento.preventDefault();
        if (!abierto) { setAbierto?.(true); return; }
        mover(-1);
        return;

      case 'Home':
        if (!abierto || !lista.length) return;
        evento.preventDefault();
        setIndiceActivo(0);
        return;

      case 'End':
        if (!abierto || !lista.length) return;
        evento.preventDefault();
        setIndiceActivo(lista.length - 1);
        return;

      case 'PageDown':
        if (!abierto || !lista.length) return;
        evento.preventDefault();
        mover(SALTO_PAGINA);
        return;

      case 'PageUp':
        if (!abierto || !lista.length) return;
        evento.preventDefault();
        mover(-SALTO_PAGINA);
        return;

      case 'Enter':
        if (!abierto || !itemActivo) return; // sin resaltado, Enter es del formulario
        evento.preventDefault();
        // Frenar la propagación: dentro de una zona de alta de renglones, el
        // contenedor también escucha Enter para agregar el renglón. Elegir una
        // opción y agregar el renglón con la misma tecla haría entrar el renglón
        // a medio completar.
        evento.stopPropagation();
        confirmar(itemActivo, evento);
        return;

      case 'Escape':
        if (!abierto) return;
        evento.preventDefault();
        evento.stopPropagation();
        setIndiceActivo(-1);
        onCerrar?.();
        return;

      case 'Tab':
        // Tab no se intercepta: debe seguir moviendo el foco. Solo se aprovecha
        // para confirmar lo que estaba resaltado antes de irse.
        if (abierto && itemActivo && cerrarAlElegir) {
          onElegir?.(itemActivo);
          setIndiceActivo(-1);
          setAbierto?.(false);
        } else if (abierto) {
          onCerrar?.();
        }
        return;

      default:
    }
  }, [abierto, cerrarAlElegir, confirmar, indiceActivo, lista, mover, onCerrar, onElegir, setAbierto]);

  return {
    indiceActivo,
    setIndiceActivo,
    onKeyDown,
    registrarOpcion,
    contenedorRef,
    // Atributos ARIA del input que comanda el desplegable. Sin esto un lector de
    // pantalla no anuncia la opción resaltada: el resaltado sería solo visual.
    propsAriaInput: {
      role: 'combobox',
      'aria-expanded': Boolean(abierto),
      'aria-autocomplete': 'list',
      'aria-activedescendant': indiceActivo >= 0 ? `opcion-${indiceActivo}` : undefined,
    },
  };
}
